import { value } from '@takeshape/util';
import urlJoin from 'url-join';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { getAuthInfoCacheExpirationForClientCredentials, getAuthInfoRequestConfigForClientCredentials, getAuthInfoRequestResultsForClientCredentials } from "../util/auth-info.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
import { fixObfuscatedProp } from "../util/props.js";
const title = 'commerce layer';
const serviceProvider = {
    name: 'commercelayer',
    title,
    description: 'commerce layer API',
    categories: ['ecommerce'],
    links: [{ title: 'Homepage', url: 'https://commercelayer.io/' }],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('commercelayer'),
    getFormSchema: value({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '^[0-9a-z_-]+$',
                default: 'commerce-layer'
            },
            title: {
                title: 'Name',
                type: 'string',
                default: 'commerce layer'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'CommerceLayer'
            },
            baseEndpoint: {
                title: 'Base endpoint',
                type: 'string',
                pattern: '^https://[a-zA-Z0-9-]+\\.commercelayer\\.io$'
            },
            scope: {
                title: 'Allowed Scopes',
                type: 'string'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    clientId: {
                        title: 'Client ID',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['clientId']
            }
        },
        required: ['title', 'id', 'namespace', 'baseEndpoint', 'scope', 'authentication']
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'baseEndpoint', 'scope', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique, url-safe identifier for this service.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            baseEndpoint: {
                widget: 'singleLineText',
                instructions: 'See [Getting Started](https://docs.commercelayer.io/api/getting-started#get-your-credentials)'
            },
            scope: {
                widget: 'singleLineText',
                instructions: 'See [Getting Started](https://docs.commercelayer.io/api/getting-started#get-your-credentials)'
            },
            authentication: {
                order: ['clientId', 'clientSecret'],
                properties: {
                    clientId: {
                        widget: 'sensitiveField',
                        instructions: 'See [Getting Started](https://docs.commercelayer.io/api/getting-started#get-your-credentials)'
                    }
                }
            }
        }
    }),
    getConfig(formData) {
        const baseEndpoint = formData.baseEndpoint;
        const scope = formData.scope;
        const authentication = formData.authentication;
        return {
            title: formData.title,
            namespace: formData.namespace,
            provider: 'commercelayer',
            serviceType: 'openapi',
            authenticationType: 'oauth2',
            authentication: {
                type: 'oauth2',
                grantType: 'clientCredentials',
                accessTokenUrl: urlJoin(baseEndpoint, 'oauth/token'),
                clientId: fixObfuscatedProp(authentication.clientId),
                scope
            },
            options: {
                endpoint: urlJoin(baseEndpoint, 'api'),
                headers: {
                    Accept: 'application/vnd.api+json'
                },
                baseEndpoint,
                spec: 'https://data.commercelayer.app/schemas/openapi.json',
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION,
                scope
            }
        };
    },
    getAuthInfoCacheExpiration: getAuthInfoCacheExpirationForClientCredentials(title),
    getAuthInfoRequestConfig: getAuthInfoRequestConfigForClientCredentials(title),
    getAuthInfoResult: getAuthInfoRequestResultsForClientCredentials(title),
    isCompleteConfig: hasEveryKey([
        'authentication',
        'options.endpoint',
        'options.headers',
        'options.baseEndpoint',
        'options.spec',
        'options.scope'
    ]),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/customers')
    })
};
export default serviceProvider;
